import { useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  FormField,
  FormItem,
} from "@/components/ui/form";
import { CustomerFormData } from "./types";
import { supabase } from "@/integrations/supabase/client";

interface PromoCodeSectionProps {
  form: UseFormReturn<CustomerFormData>;
  eventId: string;
  onPromoCodeApplied: (discountPercentage: number) => void;
}

export const PromoCodeSection = ({ form, eventId, onPromoCodeApplied }: PromoCodeSectionProps) => {
  const [showPromoCode, setShowPromoCode] = useState(false);
  const [isCheckingPromo, setIsCheckingPromo] = useState(false);
  const [promoError, setPromoError] = useState<string | null>(null);

  const checkPromoCode = async (code: string) => {
    setIsCheckingPromo(true);
    setPromoError(null);

    try {
      // Convert the input code to uppercase for case-insensitive comparison
      const upperCode = code.toUpperCase();
      console.log("Checking promo code:", upperCode, "for event:", eventId);
      
      const { data, error } = await supabase
        .from("promo_codes")
        .select("*")
        .ilike("code", upperCode) // Using ilike for case-insensitive matching
        .eq("event_id", eventId)
        .maybeSingle();

      if (error) throw error;

      if (!data) {
        setPromoError("Invalid promo code");
        return;
      }

      const now = new Date();
      if (data.valid_until && new Date(data.valid_until) < now) {
        setPromoError("Promo code has expired");
        return;
      }

      if (data.max_uses && data.current_uses >= data.max_uses) {
        setPromoError("Promo code has reached maximum uses");
        return;
      }

      onPromoCodeApplied(data.discount_percentage);
      
      // Update promo code usage in a separate operation
      supabase
        .from("promo_codes")
        .update({ current_uses: data.current_uses + 1 })
        .eq("id", data.id)
        .then(({ error }) => {
          if (error) console.error("Error updating promo code usage:", error);
        });

    } catch (error) {
      console.error("Error checking promo code:", error);
      setPromoError("Error validating promo code");
    } finally {
      setIsCheckingPromo(false);
    }
  };

  if (!showPromoCode) {
    return (
      <button
        type="button"
        onClick={() => setShowPromoCode(true)}
        className="text-purple-500 hover:text-purple-600 font-medium"
      >
        Have a promo code?
      </button>
    );
  }

  return (
    <FormField
      control={form.control}
      name="promoCode"
      render={({ field }) => (
        <FormItem>
          <div className="flex gap-2">
            <Input
              placeholder="Enter promo code"
              {...field}
              className="bg-white text-black"
            />
            <Button
              type="button"
              variant="secondary"
              onClick={() => checkPromoCode(field.value)}
              disabled={isCheckingPromo || !field.value}
            >
              Apply
            </Button>
          </div>
          {promoError && (
            <p className="text-sm text-red-500 mt-1">{promoError}</p>
          )}
        </FormItem>
      )}
    />
  );
};